import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  resetContactProfileLookup,
  resetLoginState,
  resetLogoutState,
  setIsComingFromURL,
} from "app/redux";
import { logoutResponseState } from "app/redux/features/logout";
import LogoutConfirmDialog from "./LogoutConfirmDialog";
import { ContactProfileDetailsContext } from "../macrocomponents/ContactLayout";
import { clearSessionStore, getSessionStore } from "app/utils/sessionStore";
import { Tooltip } from "primereact/tooltip";
import { THEME } from "app/utils/types";
import PrimeReact from "primereact/api";
import ExitIcon from "app/assets/IconComponent/ExitIcon";
import { clearLocalStore } from "app/utils/localStore";
import TriggerLogoutRequest from "app/redux/features/logout/action";
import { isEmpty } from "ramda";

interface SignoutLeftPanelProps {
  /**
   * Renders the advisor profile menu's row - red ExitIcon plus a "Logout" label that is
   * always visible - instead of the nav bar's own "Sign Out" tile, whose label collapses
   * with the bar. The confirm dialog and logout flow below are the same either way.
   */
  asMenuItem?: boolean;
}

function SignoutLeftPanel({ asMenuItem = false }: SignoutLeftPanelProps) {
  const { isOpen } = useContext(ContactProfileDetailsContext);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logoutSuccess, logoutRequestError } = useSelector(logoutResponseState);

  const resetTheme = () => {
    const theme = getSessionStore("globalTheme");
    if (theme === THEME.DARK) {
      window.document.documentElement.classList.remove("dark");
      PrimeReact?.changeTheme?.(`lara-dark-blue`, `lara-light-blue`, "app-theme", () => {
        const allThemeLinks = document.querySelectorAll("link#app-theme");
        if (allThemeLinks.length > 1) {
          for (let i = 1; i < allThemeLinks.length; i++) {
            allThemeLinks[i].remove();
          }
        }
      });
    }
  };

  const completeLogout = () => {
    dispatch(resetContactProfileLookup());
    clearLocalStore();
    clearSessionStore();
    dispatch(resetLoginState());
    dispatch(resetLogoutState());
    navigate("/");
  };

  useEffect(() => {
    if (!isEmpty(logoutSuccess) || !isEmpty(logoutRequestError)) {
      resetTheme();
      completeLogout();
    }
  }, [logoutRequestError, logoutSuccess]);

  const logOut = () => {
    dispatch(setIsComingFromURL(""));
    dispatch(resetContactProfileLookup());
    dispatch(TriggerLogoutRequest("logout"));
  };
  const globalTheme = getSessionStore("globalTheme");
  const iconColor = globalTheme === THEME.DARK ? "#ffffff" : "#00132e";

  return (
    <>
      <LogoutConfirmDialog
        popUpOpen={popUpOpen}
        setPopUpOpen={setPopUpOpen}
        logoutHandle={logOut}
      />
      {asMenuItem ? (
        <div
          className="logout-btn flex items-center cursor-pointer px-3 ml-1 py-1 hover:bg-red-100 dark:hover:!bg-red-100 rounded-md"
          onClick={() => setPopUpOpen(true)}
        >
          <div className="w-[20px] h-[20px] mr-2">
            <ExitIcon fill="#e24c4c" />
          </div>
          <span className="text-red-500">Logout</span>
        </div>
      ) : (
        <div
          className={`custom-target-icon flex p-2 mt-2 gap-2 items-center justify-center ${
            isOpen ? "flex-row rounded-lg" : "flex-col rounded-lg"
          } hover:cursor-pointer relative group hover:bg-gray-100 dark:hover:!bg-gray-800  `}
          onClick={() => setPopUpOpen(true)}
          data-pr-tooltip="Sign out"
        >
          <div className="w-[20px] h-[20px]">
            <ExitIcon fill={iconColor} />
          </div>
          <span
            className={`text-sm font-semibold !hover:text-dark-primary  ${
              isOpen ? "w-20" : "w-0 p-0 hidden"
            } transition-all overflow-hidden`}
          >
            Sign Out
          </span>
          {!isOpen && <Tooltip target=".custom-target-icon" className="tooltip-wrapper" />}
        </div>
      )}
    </>
  );
}

export default SignoutLeftPanel;
